/**
 * @file 任务初始化配置头部
 * @author sunyingying01
 */

import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useState} from 'react';
import {Steps, Divider, Spin, App} from 'antd';
import {LoadingOutlined} from '@ant-design/icons';
import cx from 'classnames';
import CollapsedBtn from '@components/CollapsedBtn';
import JobSelect from '@components/JobSelect';
import {JobStore} from '@/stores';
import {withUploadGuard} from '@/components/uploadGuard';
import AutoSave from '../AutoSave';
import styles from './index.module.less';

interface InitialHeaderProps {
    store: IInitialPageStore['store'];
    /** 选择任务 */
    onChangeJob: (value: JobStore) => void;
    isEmpty?: boolean;
}

const updateStepIcons = () => {
    const allStepNodes = document.querySelectorAll('.ant-steps-item');
    allStepNodes.forEach((stepNode, idx) => {
        const iconNode = stepNode.querySelector('.ant-steps-item-icon');
        if (stepNode.classList.contains('ant-steps-item-finish') && iconNode) {
            (iconNode as HTMLElement).textContent = String(idx + 1);
        }
    });
};

const StepsSection: React.FC<{
    isEmpty: boolean | undefined;
    jobsListStore: any;
    stepStore: any;
    taskRunningStore: any;
}> = observer(({isEmpty, jobsListStore, stepStore, taskRunningStore}) => {
    const [loadingPercent, setLoadingPercent] = useState(0);

    // Progress 循环动画 - 使用 25%-98% 的范围,保持一段弧形
    useEffect(() => {
        if (!taskRunningStore?.taskExecutionStore?.isInitialingPage) {
            setLoadingPercent(0);
            return;
        }

        // 初始化为 25%
        setLoadingPercent(25);

        const timer = setInterval(() => {
            setLoadingPercent(prev => {
                if (prev >= 98) {
                    return 25;
                }
                return prev + 2;
            });
        }, 30);

        return () => clearInterval(timer);
    }, [taskRunningStore?.taskExecutionStore?.isInitialingPage]);

    // 判定失败时,如果在步骤 0/1,跳转到步骤 2
    useEffect(() => {
        const isFailed = taskRunningStore?.taskExecutionStore?.isFailed;
        const currentStep = stepStore?.viewingStep;
        if (isFailed && currentStep < 2) {
            stepStore?.exitViewMode();
        }
    }, [taskRunningStore?.taskExecutionStore?.isFailed, stepStore, jobsListStore]);

    const handleStepChange = React.useCallback((current: number) => {
        const targetStep = stepStore?.steps?.[current];
        // const currentStepInfo = stepStore?.currentStepInfo;

        // 如果当前在第2步(即将开始页面),允许查看前面已完成的步骤
        if (stepStore?.currentStep === 2) {
            if (current < 2 && targetStep?.completed) {
                stepStore?.enterViewMode(current);
            }
            return;
        }

        // 只允许点击已完成的步骤
        // 如果当前步骤未完成,不允许跳转到后面的步骤
        if (stepStore?.isViewMode) {
            stepStore?.gotoStep(current);
            jobsListStore.selectedJobDetail?.fetchSaveUpdateJob();
        }
    }, [stepStore, jobsListStore]);

    // 处理点击"即将开始"tab
    const handleStartTipClick = React.useCallback(() => {
        if (stepStore?.isViewMode) {
            stepStore?.exitViewMode();
        }
    }, [stepStore]);

    if (isEmpty || !jobsListStore.selectedJobDetail) {
        return null;
    }

    // 显示当前步骤或查看步骤
    const displayStep = stepStore?.isViewMode ? stepStore?.viewingStep : stepStore?.currentStep;

    return (
        <div className={cx(styles.stepsWrap, {
            [styles.stepsWrapMore]: stepStore?.currentStep === 2,
            [styles.stepsNotClickable]: stepStore?.currentStep !== 2 && stepStore?.isViewMode,
            [styles.stepsClickable]: !stepStore?.isViewMode
        })}>
            <Steps
                size="small"
                type="navigation"
                current={displayStep || 0}
                items={stepStore?.steps || []}
                onChange={handleStepChange}
            />
            <span
                className={cx(
                    styles.startTip,
                    {[styles.startTipClickable]: stepStore?.isViewMode},
                    {[styles.disabledStartTip]: stepStore?.currentStep < 2}
                )}
                onClick={handleStartTipClick}
            >
                {taskRunningStore?.taskExecutionStore?.isFailed ? (
                    <span className={styles.filedTip}>判定失败</span>
                ) : (
                    <>
                        即将开始
                        {taskRunningStore?.taskExecutionStore?.isInitialingPage && taskRunningStore.taskExecutionStore.canTerminate && (
                            <Spin indicator={<LoadingOutlined spin />}  className={styles.startTipLoading}/>
                        )}
                    </>
                )}
            </span>
        </div>
    );
});

const renderAutoSave = (isEmpty: boolean | undefined, jobsListStore: any) => {
    if (isEmpty || !jobsListStore.selectedJobDetail) {
        return null;
    }
    return (
        <AutoSave
            saving={jobsListStore.selectedJobDetail?.saving}
            lastSavedTime={jobsListStore.selectedJobDetail?.lastSavedTime}
        />
    );
};

const InitialHeader: React.FC<InitialHeaderProps> = props => {
    const {rootStore, projectsListStore, taskRunningStore} = props.store;
    const {jobsListStore} = projectsListStore;
    const {stepStore} = jobsListStore.selectedJobDetail || {};
    const {modal} = App.useApp();

    const handleJobChange = useCallback(
        (job: JobStore) => {
            withUploadGuard(modal, () => {
                jobsListStore.setSelectedJobId(job.job_id);
                props.onChangeJob(job);
            });
        },
        [modal, jobsListStore, props]
    );

    useEffect(
        () => {
            updateStepIcons();
        },
        [stepStore?.currentStep]
    );

    return (
        <div className={styles.container}>
            <div className={styles.headerActionWrapper}>
                <div className={styles.left}>
                    <CollapsedBtn
                        store={rootStore.siderNavigationStore}
                        className={
                            rootStore.siderNavigationStore.collapsed ? '' : styles.collapsedToggleButton
                        }
                    />
                    {jobsListStore.jobsList.length > 0 && (
                        <Divider
                            type="vertical"
                            className={cx(
                                styles.divider,
                                {[styles.hiddenDivider]: !rootStore.siderNavigationStore.collapsed}
                            )}
                        />
                    )}
                </div>
                {jobsListStore.jobsList.length > 0 && (
                    <div className={cx(
                        {[styles.collapsedRightWrapper]: !rootStore.siderNavigationStore.collapsed})
                    }
                    >
                        <JobSelect
                            jobsList={jobsListStore.jobsList}
                            selectedJobId={jobsListStore.selectedJobId}
                            onChange={handleJobChange}
                        />
                    </div>
                )}
            </div>
            <StepsSection
                isEmpty={props.isEmpty}
                jobsListStore={jobsListStore}
                stepStore={stepStore}
                taskRunningStore={taskRunningStore}
            />
            {renderAutoSave(props.isEmpty, jobsListStore)}
        </div>
    );
};

export default observer(InitialHeader);
